/**
 * Manual de uso de Alexia Rest, pantalla a pantalla.
 *
 * Es UNA fuente para tres cosas (Alex, 5 sep 2026):
 *   - el panel de ayuda de cada pantalla (texto, preguntas frecuentes, vídeos),
 *   - el tour guiado que se hace al terminar el cuestionario inicial,
 *   - el prompt de Alexia cuando se le pregunta desde ese panel: sabe en qué
 *     pantalla está el usuario y cómo funciona, no solo qué datos tiene.
 *
 * Vive en `shared` porque lo leen el frontend y la API. Los vídeos los graba
 * Alex (ver VIDEOS más abajo); una pantalla sin vídeos no enseña la sección.
 *
 * Textos de todas las pantallas con manual: los de Alex (mejoras 62 y 68,
 * 6 sep 2026).
 *
 * Cómo se escribe: tuteando, frases cortas, sin jerga. Primero qué se hace
 * aquí, luego cómo, luego lo que suele fallar. Cada `howItWorks` es un
 * párrafo; las FAQ contestan en dos o tres frases.
 */

export interface HelpVideo {
  title: string;
  /** Enlace de YouTube (oculto) o Loom. El panel lo convierte a embed. */
  url: string;
  /** Duración legible, p. ej. «3 min». */
  duration?: string;
}

export interface HelpFaq {
  q: string;
  a: string;
}

export interface HelpScreen {
  /** Identificador estable: es lo que viaja a la API en el chat. */
  id: HelpScreenId;
  /** Rutas del frontend que pertenecen a esta pantalla (prefijo). */
  paths: string[];
  /** Nombre tal y como sale en el menú. */
  title: string;
  /** Una o dos frases: para qué sirve. */
  summary: string;
  /** Cómo funciona, por párrafos. */
  howItWorks: string[];
  faqs: HelpFaq[];
  /** Preguntas para el chat de esta pantalla. */
  suggestions: string[];
  videos: HelpVideo[];
}

export type HelpScreenId =
  | 'panel'
  | 'asistente'
  | 'ingredientes'
  | 'inventario'
  | 'proveedores'
  | 'recetas'
  | 'precios'
  | 'ingresos'
  | 'gastos'
  | 'analisis'
  | 'resultados'
  | 'inversiones'
  | 'objetivos'
  | 'impuestos'
  | 'ajustes';

/**
 * Dónde viven los videotutoriales. Los graba Alex y se suben UNA vez al
 * bucket de ayuda de producción (`npm run ayuda:videos -- <carpeta>` en
 * infra); CloudFront los sirve por /ayuda/* en app.alexiarest.com. La demo
 * apunta aquí también: es el único sitio donde están.
 */
export const HELP_VIDEO_BASE = 'https://app.alexiarest.com/ayuda/videos';

const video = (pantalla: string, slug: string, title: string, duration: string): HelpVideo => ({
  title,
  url: `${HELP_VIDEO_BASE}/${pantalla}/${slug}.mp4`,
  duration,
});

/** Tutoriales por pantalla (documento «Tutoriales de Alexia» de Alex, 6 sep 2026). */
const VIDEOS: Partial<Record<HelpScreenId, HelpVideo[]>> = {
  ingresos: [
    video('ingresos', 'entender-ingresos', 'Cómo entender la sección de Ingresos', '1 min'),
    video('ingresos', 'registrar-ingresos', 'Cómo registrar ingresos manualmente', '1 min'),
    video('ingresos', 'importar-ventas-excel', 'Cómo importar tus ventas desde Excel', '2 min'),
    video('ingresos', 'conectar-tpv', 'Cómo conectar tu punto de venta con Alexia', '1 min'),
  ],
  gastos: [
    video('gastos', 'entender-gastos', 'Cómo entender la sección de Gastos', '1 min'),
    video('gastos', 'registrar-gasto', 'Cómo registrar un gasto manualmente', '1 min'),
    video('gastos', 'escanear-factura', 'Cómo escanear una factura con Alexia', '1 min'),
    video('gastos', 'facturas-pendientes', 'Cómo gestionar facturas pendientes y cuentas por pagar', '1 min'),
  ],
  ingredientes: [
    video('ingredientes', 'crear-ingrediente', 'Cómo crear un ingrediente', '1 min'),
    video('ingredientes', 'importar-ingredientes-excel', 'Cómo importar tus ingredientes desde Excel', '2 min'),
    video('ingredientes', 'actualizar-precios', 'Cómo actualizar los precios de tus ingredientes', '1 min'),
    video('ingredientes', 'gestionar-ingredientes', 'Cómo gestionar tu base de ingredientes', '1 min'),
  ],
  recetas: [
    video('recetas', 'plato-y-subreceta', 'Diferencia entre un plato y una subreceta', '1 min'),
    video('recetas', 'crear-subreceta', 'Cómo crear y costear una subreceta', '2 min'),
    video('recetas', 'merma', 'Cómo funciona la merma en una receta', '1 min'),
    video('recetas', 'crear-plato', 'Cómo crear y costear un plato final', '1 min'),
    video('recetas', 'importar-recetas-excel', 'Cómo importar recetas y subrecetas desde Excel', '1 min'),
    video('recetas', 'ficha-tecnica', 'Cómo crear una ficha técnica de producción', '1 min'),
  ],
  precios: [
    video('precios', 'anadir-plato-carta', 'Cómo añadir un plato a tu carta y ponerle precio', '1 min'),
    video('precios', 'rentabilidad-plato', 'Cómo entender el análisis de rentabilidad de un plato', '1 min'),
    video('precios', 'cambios-coste-platos', 'Cómo revisar cambios en el coste de tus platos', '1 min'),
    video('precios', 'precios-por-canal', 'Cómo comparar precios y márgenes por canal', '1 min'),
  ],
  resultados: [
    video('resultados', 'leer-cuenta-resultados', 'Cómo leer tu cuenta de resultados', '1 min'),
    video('resultados', 'evolucion-rentabilidad', 'Cómo analizar la evolución de tu rentabilidad', '1 min'),
  ],
  inversiones: [
    video('inversiones', 'que-es-inversion', 'Qué es una inversión y cómo afecta a tus resultados', '1 min'),
    video('inversiones', 'registrar-inversion', 'Cómo registrar una inversión', '1 min'),
    video('inversiones', 'cuadro-depreciacion', 'Cómo leer el cuadro de depreciación', '1 min'),
  ],
  analisis: [
    video('analisis', 'punto-equilibrio-indicadores', 'Cómo entender tu punto de equilibrio y tus indicadores', '1 min'),
    video('analisis', 'cargar-ventas-por-plato', 'Cómo cargar las ventas por plato', '1 min'),
    video('analisis', 'ingenieria-menu', 'Cómo analizar tu ingeniería de menú', '2 min'),
  ],
  inventario: [
    video('inventario', 'recuento-inventario', 'Cómo hacer tu recuento de inventario', '2 min'),
    video('inventario', 'inventario-hoja-recuento', 'Cómo hacer el inventario con una hoja de recuento', '1 min'),
  ],
  asistente: [video('asistente', 'alexia-tu-asistente', 'Alexia, tu asistente', '2 min')],
  impuestos: [video('impuestos', 'repartir-impuestos', 'Cómo repartir impuestos entre periodos', '2 min')],
  proveedores: [video('proveedores', 'gestionar-proveedores', 'Cómo gestionar tus proveedores', '1 min')],
  objetivos: [video('objetivos', 'definir-objetivos', 'Cómo definir y revisar tus objetivos', '1 min')],
  panel: [video('panel', 'usar-panel-general', 'Cómo usar el panel general', '1 min')],
};

export const HELP_SCREENS: HelpScreen[] = [
  {
    id: 'panel',
    paths: ['/'],
    title: 'Panel general',
    summary:
      'La foto del negocio en el periodo que elijas arriba: ventas, costes y rentabilidad, con semáforo frente a tus objetivos.',
    howItWorks: [
      'El panel general reúne los principales datos de tu negocio para que puedas entender rápidamente qué está pasando en el periodo seleccionado.',
      'Aquí puedes ver indicadores de ventas, costes y rentabilidad, como ingresos, ticket promedio, coste de materia prima, coste de personal, Prime Cost, EBITDA, beneficio neto y margen de seguridad.',
      'Si has definido objetivos, Alexia compara tus resultados con ellos para mostrarte qué métricas están dentro de objetivo y cuáles necesitan atención.',
      'En «Necesita tu atención» encontrarás avisos importantes, como facturas vencidas, cambios en el coste de tus platos o márgenes que se han salido de los límites que has configurado. Cada aviso te lleva directamente al lugar donde puedes revisarlo.',
      'También encontrarás preguntas sugeridas para Alexia y diferentes gráficos para analizar la evolución de tus ingresos y gastos, el rendimiento por día de la semana, tus costes fijos y variables y los platos que más están aportando al negocio.',
      'La idea es sencilla: entrar, entender cómo va el negocio y saber dónde merece la pena mirar primero.',
    ],
    faqs: [
      {
        q: '¿Qué periodo estoy viendo?',
        a: 'Todos los datos del panel corresponden al periodo seleccionado en la parte superior. Puedes utilizar periodos predefinidos o elegir directamente las fechas que quieras analizar.',
      },
      {
        q: '¿Por qué algunos indicadores aparecen en verde y otros en rojo?',
        a: 'Cuando tienes un objetivo definido, Alexia compara el resultado real con ese objetivo. El verde indica que la métrica está dentro del objetivo y el rojo que necesita atención. Ten en cuenta que no todos los objetivos funcionan igual: en ventas normalmente quieres estar por encima, mientras que en costes normalmente quieres mantenerte por debajo.',
      },
      {
        q: '¿De dónde salen los datos del panel?',
        a: 'El panel utiliza la información que tienes registrada en las distintas secciones de Alexia. Por ejemplo, las ventas vienen de Ingresos, los costes y gastos de Gastos, el consumo de materia prima también puede tener en cuenta Inventario, y los costes de los platos provienen de Ingredientes, Recetas y Precios de la carta. Si falta información en alguna sección, algunos indicadores también pueden estar incompletos.',
      },
      {
        q: '¿Qué significa «Necesita tu atención»?',
        a: 'Es una selección de situaciones que Alexia considera conveniente que revises. Por ejemplo, una factura vencida, un plato cuyo coste ha cambiado significativamente o un margen que ha caído por debajo del límite que habías definido. No significa necesariamente que tengas que hacer un cambio: sirve para ayudarte a encontrar rápidamente dónde mirar.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: 'Es la suma del coste de materia prima y el coste de personal. Te permite ver rápidamente qué porcentaje de tus ventas estás destinando conjuntamente a producto y equipo.',
      },
      {
        q: '¿Qué es el margen de seguridad?',
        a: 'Indica cuánto podrían caer tus ventas antes de llegar al punto de equilibrio y empezar a generar pérdidas. Cuanto mayor sea el margen de seguridad, mayor distancia tienes respecto a ese límite.',
      },
      {
        q: '¿Qué significa «Los platos que más aportan»?',
        a: 'Son los platos ordenados según su contribución total al negocio. No solo importa cuánto margen deja una unidad, sino también cuántas unidades se venden. Por eso un plato con un margen unitario menor puede aportar más dinero al negocio si se vende muchas más veces.',
      },
      {
        q: '¿Por qué puedo vender más y aun así ganar menos?',
        a: 'Porque las ventas son solo una parte del resultado. También pueden haber aumentado el coste de materia prima, el coste de personal, otros gastos o haberse reducido tus márgenes. Puedes revisar Cuenta de resultados o preguntarle directamente a Alexia para identificar qué ha cambiado.',
      },
      {
        q: '¿Qué puedo preguntarle a Alexia desde el panel?',
        a: 'Puedes utilizar las preguntas que Alexia te propone o hacer las tuyas. Por ejemplo: ¿por qué ha bajado mi beneficio?, ¿qué facturas tengo vencidas?, ¿qué costes se han disparado?, ¿qué platos debería revisar?, ¿voy a tener problemas de caja? Si quieres profundizar, puedes abrir el asistente y continuar allí el análisis.',
      },
      {
        q: '¿Tengo que revisar todo el panel cada día?',
        a: 'No. La idea del panel es precisamente evitarlo. Empieza por «Necesita tu atención» y por aquellos indicadores que estén fuera de tus objetivos. A partir de ahí puedes profundizar solo en aquello que realmente necesite revisión.',
      },
    ],
    suggestions: [
      '¿Cómo va el mes comparado con el anterior?',
      '¿Qué es lo que más me está costando?',
      '¿Qué significa el margen de seguridad?',
    ],
    videos: VIDEOS.panel ?? [],
  },
  {
    id: 'asistente',
    paths: ['/asistente'],
    title: 'Alexia, tu asistente',
    summary:
      'Un chat para preguntar en lenguaje normal cómo va el negocio. Alexia responde con tus números reales, no con generalidades.',
    howItWorks: [
      'Alexia analiza la información que tienes cargada en tu cuenta para ayudarte a entender qué está pasando en tu negocio y qué podrías revisar.',
      'Puedes preguntarle por tus ventas, gastos, rentabilidad, facturas pendientes, costes de platos o cualquier otro dato disponible en Alexia.',
      'Las respuestas tienen en cuenta el periodo que tengas seleccionado en la parte superior.',
      'También puedes preguntarle cómo utilizar la propia plataforma: por ejemplo, cómo importar ventas, hacer un inventario, crear una receta o encontrar una configuración.',
      'Alexia puede ayudarte a interpretar información y explorar posibles acciones, pero no modifica tus datos ni toma decisiones por ti.',
      'Y si no consigue resolver tu duda, siempre puedes escribirnos desde Soporte para que te ayude una persona de nuestro equipo.',
    ],
    faqs: [
      {
        q: '¿Qué tipo de cosas puedo preguntarle a Alexia?',
        a: 'Puedes preguntarle tanto por los datos de tu negocio como por el funcionamiento de la plataforma. Por ejemplo: ¿por qué bajó mi beneficio este mes?, ¿qué facturas tengo pendientes?, ¿qué platos han cambiado de coste?, ¿estoy por encima de mi punto de equilibrio?, ¿cómo hago un inventario?, ¿cómo importo mis ventas? Cuanto más contexto le des en tu pregunta, más útil podrá ser la respuesta.',
      },
      {
        q: '¿De dónde saca Alexia la información?',
        a: 'Alexia utiliza los datos que tienes registrados dentro de tu cuenta. Por eso, si todavía no has cargado ingresos, gastos, costes u otra información necesaria, algunas respuestas pueden ser limitadas.',
      },
      {
        q: '¿Qué periodo está analizando Alexia?',
        a: 'Alexia utiliza el periodo que tengas seleccionado en la parte superior de la pantalla. Si quieres analizar otro mes, varios meses o un periodo concreto, cambia las fechas antes de hacer tu pregunta.',
      },
      {
        q: '¿Por qué Alexia me propone preguntas automáticamente?',
        a: 'Las preguntas sugeridas se generan en función de los datos y situaciones que Alexia detecta en tu negocio. Puedes utilizarlas para empezar a explorar o ignorarlas y escribir tu propia pregunta.',
      },
      {
        q: '¿Alexia puede cambiar mis datos?',
        a: 'No. Alexia puede analizar, explicar y ayudarte a pensar posibles acciones, pero no modificará tus ingresos, gastos, recetas, precios ni ninguna otra información por su cuenta.',
      },
      {
        q: '¿Puedo preguntarle cómo usar Alexia?',
        a: 'Sí. Alexia conoce el funcionamiento de la plataforma y puede ayudarte con dudas como cómo importar ventas, crear una subreceta, actualizar ingredientes o realizar un inventario.',
      },
      {
        q: '¿Puedo confiar siempre en lo que me responde?',
        a: 'Alexia utiliza inteligencia artificial y puede equivocarse. Utiliza sus respuestas como apoyo para analizar tu negocio y revisa siempre la información antes de tomar decisiones importantes.',
      },
      {
        q: '¿Qué hago si Alexia no resuelve mi duda?',
        a: 'Puedes reformular la pregunta con más detalle. Y si sigues necesitando ayuda, utiliza Soporte para escribir directamente a nuestro equipo.',
      },
    ],
    suggestions: [
      '¿Qué tipo de preguntas puedo hacerte?',
      '¿Cuántas preguntas me quedan este mes?',
      '¿Puedes cambiar un precio por mí?',
    ],
    videos: VIDEOS.asistente ?? [],
  },
  {
    id: 'ingredientes',
    paths: ['/ingredientes'],
    title: 'Ingredientes',
    summary:
      'El catálogo de lo que compras, con un solo precio por ingrediente: el vigente y sin IVA. De aquí sale el coste de cada plato.',
    howItWorks: [
      'Aquí puedes crear y mantener tu base de datos de ingredientes.',
      'Para cada ingrediente debes indicar cómo lo compras realmente: proveedor, formato de compra, cantidad, unidad y precio. Por ejemplo, una garrafa de aceite de 5 litros o una caja de 24 botellas.',
      'Puedes introducir el precio con IVA o sin IVA. Alexia calculará automáticamente el otro y utilizará siempre el precio sin IVA para calcular el coste de tus recetas.',
      'Si tienes muchos ingredientes, puedes importarlos de forma masiva desde Excel. Y cuando necesites actualizar precios, puedes descargar tu catálogo, modificar únicamente los precios que hayan cambiado y volver a subirlo.',
      'Cada vez que actualices el precio de un ingrediente, Alexia recalculará automáticamente el coste de las recetas y platos que lo utilizan.',
      'También puedes organizar tus ingredientes por grupos. Si necesitas crear grupos nuevos, puedes hacerlo desde Ajustes.',
    ],
    faqs: [
      {
        q: '¿Qué tengo que poner como formato de compra?',
        a: 'Indica cómo compras realmente ese producto a tu proveedor. Por ejemplo: una caja, una bolsa, una botella, una garrafa o un saco. Después indica cuánto producto contiene ese formato y en qué unidad.',
      },
      {
        q: '¿Y si compro un ingrediente a granel?',
        a: 'Selecciona «a granel» como formato e indica la unidad en la que lo compras, por ejemplo kilos o litros, junto con su precio.',
      },
      {
        q: '¿Puedo introducir el precio con IVA?',
        a: 'Sí. Puedes introducir el precio con IVA o sin IVA. Alexia calculará automáticamente el otro utilizando el porcentaje de IVA que indiques. Para el cálculo de tus recetas se utilizará siempre el precio sin IVA.',
      },
      {
        q: '¿Para qué sirve la densidad?',
        a: 'La densidad permite convertir correctamente entre unidades de peso y volumen. Puede ser útil, por ejemplo, en líquidos como aceites o salsas cuando una receta utiliza gramos pero tú compras el producto en litros. Es un dato opcional y no necesitas utilizarlo en todos los ingredientes.',
      },
      {
        q: 'Compro el mismo ingrediente a dos proveedores con precios distintos, ¿qué hago?',
        a: 'Mantén en Alexia el precio que quieras utilizar como referencia para calcular tus costes (recomendación: usa el más caro siempre que puedas). Si cambias de proveedor o cambia el precio de compra, puedes editar el ingrediente y actualizarlo.',
      },
      {
        q: '¿Qué pasa cuando cambio el precio de un ingrediente?',
        a: 'Alexia recalcula automáticamente el coste de todas las recetas y platos que utilizan ese ingrediente. Si el cambio provoca una desviación importante en algún plato, podrá aparecerte una alerta según los límites que tengas configurados.',
      },
      {
        q: '¿Cómo actualizo muchos precios a la vez?',
        a: 'Pulsa en «Actualizar ingredientes» y descarga tu catálogo. Modifica en el archivo únicamente los precios que hayan cambiado, guárdalo y vuelve a subirlo a Alexia. Antes de aplicar los cambios, podrás revisar qué ingredientes se van a actualizar.',
      },
      {
        q: '¿Puedo borrar un ingrediente que ya está utilizado en recetas?',
        a: 'No: si el ingrediente forma parte de alguna receta o subreceta, Alexia no te permitirá borrarlo y te dirá en cuáles está. Quítalo de esas recetas primero, o cámbialo por otro ingrediente, y después podrás eliminarlo.',
      },
    ],
    suggestions: [
      '¿Cómo meto un precio que tengo con IVA?',
      '¿Qué ingredientes han subido de precio últimamente?',
      '¿Cómo actualizo todos los precios de golpe?',
    ],
    videos: VIDEOS.ingredientes ?? [],
  },
  {
    id: 'inventario',
    paths: ['/inventario'],
    title: 'Inventario',
    summary:
      'El recuento de lo que te queda en el almacén al cerrar el mes. Con él, la cuenta de resultados enseña lo que has consumido, no solo lo que has comprado.',
    howItWorks: [
      'Aquí puedes registrar cuánto producto te queda al cierre de cada periodo para que Alexia calcule correctamente tu consumo real de materia prima.',
      'Lo que compras durante un mes no es necesariamente lo mismo que consumes. Por eso, Alexia utiliza tus compras junto con las existencias iniciales y finales para calcular cuánto producto has utilizado realmente y reflejarlo en tu cuenta de resultados.',
      'Puedes hacer el recuento directamente en Alexia indicando cuánto te queda de cada ingrediente, ya sea por unidades como kilos, litros o piezas, o utilizando su formato de compra: cajas, garrafas, sacos, botellas, etc.',
      'También puedes descargar una hoja de recuento, rellenarla en papel y volver a subirla para que Alexia lea las cantidades. Antes de guardar nada, siempre podrás revisarlas.',
      'Una vez guardado el inventario, podrás consultarlo después desde Historial. Cada recuento conserva el valor que tenían los ingredientes en ese momento.',
    ],
    faqs: [
      {
        q: '¿Por qué es importante hacer inventario?',
        a: 'Porque comprar producto no significa haberlo consumido. Si compras 5.000 $ de materia prima pero terminas el mes con parte todavía en almacén, no tendría sentido considerar los 5.000 $ como consumo. El inventario permite que Alexia calcule cuánto producto has consumido realmente durante el periodo.',
      },
      {
        q: '¿Tengo que contar todos los ingredientes?',
        a: 'Lo ideal es registrar todos los productos que tengan existencias al cierre. Los ingredientes que dejes a cero no se incluirán en el valor final del inventario. Si quieres que el consumo de materia prima sea lo más preciso posible, cuanto más completo sea el recuento, mejor.',
      },
      {
        q: '¿Qué pasa si no hago inventario?',
        a: 'Alexia no podrá ajustar las compras por las existencias que todavía tienes en almacén. Por tanto, tu cuenta de resultados puede mostrar como consumo producto que realmente todavía no has utilizado.',
      },
      {
        q: '¿Puedo contar por cajas, garrafas o sacos?',
        a: 'Sí. Activa «Contar por formato de compra» y podrás introducir cantidades como 3 cajas, 1,5 garrafas o 2 sacos. Alexia hará automáticamente la conversión utilizando el formato que tengas configurado en cada ingrediente.',
      },
      {
        q: '¿Puedo contar directamente en kilos, litros o piezas?',
        a: 'Sí. Puedes desactivar el recuento por formato de compra e introducir directamente la cantidad que te queda utilizando la unidad del ingrediente.',
      },
      {
        q: 'No me aparece un ingrediente para contar, ¿qué reviso?',
        a: 'Comprueba primero que el ingrediente esté correctamente dado de alta dentro de Ingredientes. Si sigue sin aparecer, puedes preguntarle a Alexia o escribirnos desde Soporte para revisarlo.',
      },
      {
        q: '¿Puedo hacer el inventario en papel?',
        a: 'Sí. Pulsa en «Hoja para imprimir» para generar una hoja de recuento con tus ingredientes. Después puedes completar el inventario en papel y volver a cargarlo desde «Subir hoja rellenada».',
      },
      {
        q: '¿Alexia guarda automáticamente lo que lee de la hoja?',
        a: 'No. Alexia preparará las cantidades que haya podido interpretar, pero antes de guardar el recuento tendrás una pantalla de revisión para corregir cualquier dato.',
      },
      {
        q: '¿Qué pasa si cambia el precio de un ingrediente después de hacer el inventario?',
        a: 'El inventario histórico mantiene el valor que tenía el ingrediente en el momento en que guardaste ese recuento. Actualizar después el precio de compra no modifica el valor de inventarios anteriores.',
      },
      {
        q: '¿Dónde veo inventarios anteriores?',
        a: 'En la pestaña Historial. Ahí puedes consultar los recuentos que hayas guardado anteriormente y revisar su valor.',
      },
      {
        q: '¿Cómo afecta el inventario a mi cuenta de resultados?',
        a: 'Alexia utiliza las existencias iniciales, las compras del periodo y las existencias finales para calcular el consumo de materia prima. De esta forma, la cuenta de resultados refleja lo que realmente has consumido y no simplemente todo lo que has comprado.',
      },
    ],
    suggestions: [
      '¿Cómo afecta el inventario a la cuenta de resultados?',
      '¿Cómo subo la hoja de recuento rellenada?',
      '¿Cuánto consumí de materia prima el mes pasado?',
    ],
    videos: VIDEOS.inventario ?? [],
  },
  {
    id: 'proveedores',
    paths: ['/proveedores'],
    title: 'Proveedores',
    summary: 'La lista de a quién compras. Se usa al dar de alta ingredientes y al registrar gastos.',
    howItWorks: [
      'Aquí puedes guardar y consultar todos los proveedores que utilizas dentro de Alexia.',
      'Puedes crear un proveedor directamente desde esta sección o hacerlo mientras registras un gasto o das de alta un ingrediente. En ambos casos quedará guardado automáticamente en tu lista.',
      'Para cada proveedor puedes añadir datos de contacto y notas internas, como horarios de pedido, días de entrega, condiciones especiales o cualquier información útil para tu equipo.',
      'También puedes ver cuántos ingredientes tienes asociados a cada proveedor (y pulsar en ese número para verlos), buscar uno concreto y editar su información cuando lo necesites.',
      'La idea es que tengas toda la información de tus proveedores centralizada en un mismo lugar.',
    ],
    faqs: [
      {
        q: '¿Tengo que crear los proveedores desde esta sección?',
        a: 'No. Puedes crearlos directamente desde Proveedores, pero también mientras registras un gasto o das de alta un ingrediente. Si creas uno desde otra sección, aparecerá automáticamente aquí.',
      },
      {
        q: '¿Qué información puedo guardar de un proveedor?',
        a: 'Puedes guardar sus datos de contacto y añadir notas internas. Por ejemplo, quién es tu persona de contacto, teléfono, días de reparto, horarios para hacer pedidos, condiciones especiales o cualquier información que te ayude a trabajar con ese proveedor.',
      },
      {
        q: '¿Para qué sirve el número de ingredientes que aparece junto al proveedor?',
        a: 'Indica cuántos ingredientes tienes actualmente asociados a ese proveedor dentro de Alexia. Púlsalo y verás cuáles son en Ingredientes, con el filtro de ese proveedor ya aplicado.',
      },
      {
        q: '¿Puedo cambiar el proveedor de un ingrediente?',
        a: 'Sí. Edita el ingrediente correspondiente y selecciona el nuevo proveedor. La información del proveedor se gestiona desde esta sección, pero la relación con cada producto se cambia desde Ingredientes.',
      },
      {
        q: '¿Puedo editar un proveedor después de crearlo?',
        a: 'Sí. Pulsa en Editar para modificar sus datos de contacto, notas o cualquier otra información disponible.',
      },
      {
        q: '¿Puedo borrar un proveedor?',
        a: 'Sí, siempre que no esté asignado a ningún ingrediente. Si lo está, cambia primero esos ingredientes de proveedor y después podrás eliminarlo.',
      },
      {
        q: '¿Puedo tener dos proveedores para el mismo ingrediente?',
        a: 'Actualmente cada ingrediente tiene un proveedor asociado como referencia. Si cambias de proveedor, puedes editar el ingrediente y seleccionar el nuevo.',
      },
    ],
    suggestions: [
      '¿A qué proveedor le compro más?',
      '¿Qué facturas tengo pendientes con un proveedor?',
    ],
    videos: VIDEOS.proveedores ?? [],
  },
  {
    id: 'recetas',
    paths: ['/recetas'],
    title: 'Recetas y costeo',
    summary:
      'El escandallo de cada plato: qué lleva, cuánta merma tiene cada ingrediente y cuánto cuesta cada ración. Con subrecetas para salsas, masas y fondos.',
    howItWorks: [
      'Aquí puedes crear, costear y mantener tus platos y subrecetas.',
      'Una subreceta es una elaboración que después puedes utilizar dentro de uno o varios platos, como una salsa, una masa o una crema. Un plato es la receta final que después puedes llevar a tu carta.',
      'Para cada receta defines su rendimiento, porciones y margen de seguridad, y después añades los ingredientes y subrecetas que la componen.',
      'En cada ingrediente puedes indicar la cantidad neta que realmente necesitas y su merma de preelaboración. Alexia calculará automáticamente cuánto producto necesitas comprar y su coste real dentro de la receta.',
      'Las subrecetas pueden calcularse por unidad de rendimiento (por ejemplo, coste por kilo o litro), mientras que en los platos finales normalmente interesa conocer el coste por porción.',
      'También puedes importar recetas de forma masiva desde Excel y completar cada receta con su preparación, fotografía y alérgenos para generar una ficha técnica de producción.',
      'Cuando cambia el precio de un ingrediente, Alexia actualiza automáticamente el coste de las recetas y subrecetas que lo utilizan.',
    ],
    faqs: [
      {
        q: '¿Cuál es la diferencia entre un plato y una subreceta?',
        a: 'Un plato es la receta final que después puedes poner en tu carta. Una subreceta es una elaboración que utilizas dentro de uno o varios platos, como una salsa, un alioli, una masa o un fondo. Siempre que puedas, es recomendable crear primero las subrecetas y después utilizarlas para construir los platos finales.',
      },
      {
        q: '¿Cuál es la diferencia entre rendimiento y porciones?',
        a: 'El rendimiento indica cuánto producto obtienes al preparar la receta completa. Por ejemplo, una salsa puede rendir 800 gramos. Las porciones indican cuántas unidades de servicio obtienes de esa preparación. Dependiendo del tipo de receta, puedes utilizar uno u otro dato para calcular el coste que necesitas.',
      },
      {
        q: '¿Cuándo calculo por rendimiento y cuándo por porción?',
        a: 'Utiliza coste por rendimiento cuando después vayas a utilizar esa elaboración por peso o volumen. Por ejemplo, una crema que utilizarás en cantidades de 30 gramos dentro de diferentes platos. Utiliza coste por porción cuando quieras saber cuánto cuesta cada unidad que vas a servir o vender.',
      },
      {
        q: '¿Cómo funciona la merma?',
        a: 'La merma representa la parte del ingrediente que compras pero que no utilizas en esa preparación. Si necesitas 60 gramos de hojas de cilantro pero tienes que comprar 100 gramos porque descartas los tallos, Alexia tendrá en cuenta esa diferencia para calcular el coste real. La merma se define según cómo utilizas ese ingrediente en esa receta, por eso puede ser diferente en distintas preparaciones.',
      },
      {
        q: '¿La pérdida de peso durante la cocción es merma?',
        a: 'En esta sección, la merma se utiliza para reflejar principalmente la merma de preelaboración: pieles, tallos, huesos u otras partes que descartas antes de cocinar. No debes utilizarla simplemente para reflejar la pérdida de peso que ocurre durante la cocción.',
      },
      {
        q: 'Me sale un error de unidades al costear, ¿qué reviso?',
        a: 'Comprueba que la unidad que estás utilizando sea compatible con la forma en la que tienes registrado el ingrediente. Por ejemplo, peso con peso o volumen con volumen. Si necesitas convertir entre peso y volumen, como ocurre con algunos líquidos, puede ser necesario indicar la densidad del ingrediente.',
      },
      {
        q: '¿Qué pasa si cambia el precio de un ingrediente?',
        a: 'Alexia recalculará automáticamente el coste de todas las recetas y subrecetas que utilizan ese ingrediente. Si una subreceta cambia de coste, ese cambio también se trasladará a los platos que la utilizan.',
      },
      {
        q: '¿Puedo incluir el empaque en el coste de un plato?',
        a: 'Sí. Puedes añadir costes de empaque, como envases o bolsas de delivery, para que formen parte del coste total del plato sin tratarlos como ingredientes de la preparación.',
      },
      {
        q: '¿Puedo importar mis recetas desde Excel?',
        a: 'Sí. Desde la opción de importar puedes descargar una plantilla y cargar de forma masiva tanto platos como subrecetas. Cada fila representa uno de los componentes de la receta y, antes de guardar nada, Alexia te mostrará si existe algún dato que necesitas revisar.',
      },
      {
        q: '¿Puedo crear fichas técnicas desde Alexia?',
        a: 'Sí. Dentro de cada receta puedes añadir el procedimiento, una fotografía y los alérgenos. Después puedes descargar una ficha técnica con la información de producción para imprimirla o compartirla con tu equipo.',
      },
    ],
    suggestions: [
      '¿Cómo pongo la merma de un ingrediente?',
      '¿Qué receta me cuesta más por ración?',
      '¿Cómo creo una subreceta y la uso en un plato?',
    ],
    videos: VIDEOS.recetas ?? [],
  },
  {
    id: 'precios',
    paths: ['/precios'],
    title: 'Precios de la carta',
    summary:
      'Qué margen deja cada plato en cada canal, descontando el IVA, la comisión de cobro y la de cada plataforma. Un plato rentable en sala puede perder dinero en delivery.',
    howItWorks: [
      'Aquí puedes añadir tus platos a la carta, ponerles precio y analizar cuánto margen te deja cada uno.',
      'Cada plato se enlaza con una receta ya creada, y puedes definir su nombre en carta, grupo, precio de venta, IVA y objetivo de food cost.',
      'Si vendes por distintos canales, puedes poner un precio diferente para cada uno. Alexia tendrá en cuenta las comisiones de cobro y delivery para calcular el margen real que te deja cada plato.',
      'Cuando cambia el coste de una receta, Alexia compara el nuevo coste con el que tenías como referencia y te avisa si hay una desviación importante.',
      'También puedes consultar el precio sugerido en función de tu objetivo de food cost y comparar tus márgenes entre canales.',
    ],
    faqs: [
      {
        q: '¿El precio de venta se introduce con IVA o sin IVA?',
        a: 'El precio de venta se introduce con IVA, tal y como lo ve el cliente en la carta. Alexia calculará automáticamente el precio sin IVA para realizar los análisis de margen y rentabilidad.',
      },
      {
        q: '¿Tengo que poner el mismo precio en el local y en delivery?',
        a: 'No. Puedes definir un precio distinto para cada canal. Alexia tendrá en cuenta las comisiones asociadas a cada uno para que puedas comparar cuánto margen te deja realmente el mismo plato en sala, Uber u otras plataformas.',
      },
      {
        q: '¿Por qué un plato puede tener buen margen en sala y malo en delivery?',
        a: 'Porque en delivery pueden existir comisiones adicionales que reducen el margen. Aunque el coste de la receta sea el mismo, el resultado puede cambiar mucho dependiendo de la comisión de cada plataforma y del precio que hayas puesto en ese canal.',
      },
      {
        q: '¿Qué significa el objetivo de food cost?',
        a: 'Es el porcentaje de coste sobre venta que te gustaría conseguir para ese plato. Alexia utiliza ese objetivo para calcular un precio de referencia que te permita acercarte al food cost que has definido. No significa que tengas que utilizar obligatoriamente ese precio.',
      },
      {
        q: '¿Qué pasa cuando cambia el coste de un plato?',
        a: 'Cuando cambia el precio de alguno de sus ingredientes o subrecetas, Alexia recalcula automáticamente el coste del plato. Si el cambio supera los límites que tienes configurados, te avisará para que puedas revisarlo.',
      },
      {
        q: '¿Qué significa aceptar el nuevo coste?',
        a: 'Significa que has revisado el cambio y quieres utilizar ese coste como nueva referencia. A partir de ese momento, Alexia comparará los futuros cambios con ese nuevo coste. Aceptar el coste no obliga a cambiar el precio de venta.',
      },
      {
        q: '¿Por qué Alexia me avisa de que un plato tiene poco margen?',
        a: 'Porque el margen del plato está por debajo del mínimo que tienes configurado como referencia. La alerta no significa necesariamente que tengas que subir el precio. Sirve para señalarte qué platos merece la pena revisar.',
      },
      {
        q: '¿Qué puedo hacer si un plato tiene poco margen?',
        a: 'Puedes revisar varias cosas: el coste de la receta, las cantidades utilizadas, el precio de venta o las comisiones del canal. Si quieres profundizar, también puedes preguntarle a Alexia y analizar con ella qué opciones tienes para mejorar el resultado.',
      },
    ],
    suggestions: [
      '¿Qué platos pierden dinero en delivery?',
      '¿A qué precio tendría que vender este plato para un food cost del 30 %?',
      '¿Qué platos tienen el margen más bajo?',
    ],
    videos: VIDEOS.precios ?? [],
  },
  {
    id: 'ingresos',
    paths: ['/ingresos'],
    title: 'Ingresos',
    summary:
      'Las ventas de cada día, por método de cobro y por plataforma, con comensales y mesas. Es la base de todo lo financiero.',
    howItWorks: [
      'Aquí puedes registrar y consultar las ventas de tu negocio por día, método de pago y canal.',
      'Selecciona el periodo que quieras consultar. Verás tus ingresos totales, cortesías y descuentos, tickets promedio y el detalle diario de tus ventas.',
      'Puedes cargar tus ingresos de tres formas: manualmente, escribiendo las ventas directamente en cada día; importando un Excel o CSV con el informe de ventas de tu punto de venta; o mediante una integración, si tienes conectado tu TPV con Alexia.',
      'Los ingresos deben registrarse sin IVA. Si importas un archivo que incluye IVA, Alexia te preguntará antes de procesarlo.',
      'También puedes registrar cortesías, descuentos, número de clientes y mesas. Con estos datos, Alexia calculará automáticamente tus tickets promedio y el reparto de ventas por método de pago y canal.',
    ],
    faqs: [
      {
        q: '¿Las ventas deben introducirse con IVA o sin IVA?',
        a: 'En Alexia, los ingresos se registran y analizan sin IVA. Si introduces los datos manualmente, utiliza siempre el importe neto, sin impuestos. Si importas un archivo que contiene ventas con IVA, Alexia te preguntará antes de procesarlo y realizará el ajuste correspondiente.',
      },
      {
        q: '¿Las cortesías y los descuentos se restan de las ventas?',
        a: 'No. Las cortesías y los descuentos se muestran como información para que puedas controlar cuánto dinero estás destinando a estos conceptos, pero no se vuelven a restar del total de ventas.',
      },
      {
        q: '¿Qué hago si mi TPV no está integrado con Alexia?',
        a: 'Puedes seguir cargando tus ventas manualmente o importar un archivo Excel o CSV desde la opción «Importar ventas». También puedes escribirnos desde Soporte para solicitar una nueva integración con tu punto de venta.',
      },
      {
        q: '¿Qué ocurre si importo ventas de un día que ya tiene datos?',
        a: 'Antes de guardar la importación, Alexia te avisará de que ese día ya contiene información. Podrás revisar el resumen y decidir si quieres sustituir los datos existentes o no.',
      },
    ],
    suggestions: [
      '¿Cómo importo las ventas de mi TPV?',
      '¿Qué día de la semana vendo más?',
      '¿Cuál es mi ticket medio este mes?',
    ],
    videos: VIDEOS.ingresos ?? [],
  },
  {
    id: 'gastos',
    paths: ['/gastos'],
    title: 'Gastos',
    summary:
      'Todo lo que pagas, clasificado en el plan de cuentas del restaurante y con su estado: pagado o pendiente, con factura o sin ella.',
    howItWorks: [
      'Aquí puedes registrar y consultar los gastos de tu negocio, controlar qué tienes pendiente de pagar y ver cómo se distribuyen entre costes fijos y variables.',
      'Puedes registrar un gasto manualmente o, si tienes disponible el escáner de facturas, subir una foto o PDF para que Alexia lea los datos y prepare el registro por ti.',
      'Cada gasto debe tener una categoría, un proveedor y un importe. Los importes se registran sin IVA, ya que son los que Alexia utilizará para construir tu cuenta de resultados.',
      'Si el gasto es a crédito, puedes añadir su fecha de vencimiento. Así Alexia podrá mostrarte qué facturas siguen pendientes y cuáles ya han vencido.',
      'También puedes indicar si la factura ya ha sido recibida o sigue pendiente y actualizar su estado más adelante.',
      'Las categorías que aparecen por defecto se pueden personalizar desde Ajustes.',
    ],
    faqs: [
      {
        q: '¿Cómo sé si un gasto es fijo o variable?',
        a: 'Un coste fijo normalmente no cambia directamente en función de cuánto vendas, como el alquiler o determinados salarios. Un coste variable sí suele aumentar o disminuir con la actividad del negocio, como las materias primas. Alexia te sugerirá una clasificación según la categoría que elijas, pero puedes modificarla si lo necesitas.',
      },
      {
        q: '¿Los gastos se registran con IVA o sin IVA?',
        a: 'En Alexia, los gastos se registran sin IVA para que ese sea el importe que se refleje en tu cuenta de resultados. Si utilizas el escáner de facturas, Alexia puede leer también el IVA para ayudarte con el registro, pero el análisis de resultados utilizará el importe sin IVA.',
      },
      {
        q: '¿Qué diferencia hay entre factura pendiente y pago pendiente?',
        a: 'Son dos cosas diferentes. Una factura puede estar pendiente porque todavía no has recibido el documento del proveedor. Y un pago puede estar pendiente porque ya tienes la factura, pero todavía no la has pagado. Alexia te permite controlar ambos estados por separado.',
      },
      {
        q: '¿Cómo marco una factura como pagada?',
        a: 'Abre el gasto correspondiente y cambia el estado del pago de «Pendiente» a «Pagado». No necesitas volver a registrar el gasto.',
      },
      {
        q: '¿Qué pasa si una factura vence y todavía no la he pagado?',
        a: 'Si registraste el gasto a crédito y añadiste una fecha de vencimiento, Alexia podrá identificar cuándo el pago sigue pendiente y cuándo ya está vencido. Lo verás dentro de Cuentas por pagar.',
      },
      {
        q: '¿Puedo crear mis propias categorías de gasto?',
        a: 'Sí. Alexia incluye categorías por defecto, pero puedes crear o modificar tus propias categorías y subcategorías desde Ajustes.',
      },
      {
        q: '¿Una inversión, como un horno o una reforma, se registra como gasto?',
        a: 'Normalmente no debería cargarse completa como un gasto corriente del periodo. Las inversiones se registran desde la sección Inversiones, donde Alexia puede distribuir su coste a lo largo de su vida útil mediante la depreciación.',
      },
      {
        q: '¿Dónde registro la nómina?',
        a: 'Dentro de Gastos, selecciona la categoría correspondiente a Personal y la subcategoría que corresponda, por ejemplo sueldos y salarios o seguridad social. Esos datos después aparecerán agrupados dentro del coste de personal en tu cuenta de resultados.',
      },
      {
        q: '¿Qué pasa si todavía no tengo la factura del proveedor?',
        a: 'Puedes registrar igualmente el gasto y marcar el estado como «Factura pendiente» o «Sin factura». Cuando recibas la factura, abre el gasto y cambia su estado a «Factura recibida».',
      },
    ],
    suggestions: [
      '¿Qué facturas tengo vencidas?',
      '¿En qué categoría de gasto me he pasado este mes?',
      '¿Cómo escaneo una factura?',
    ],
    videos: VIDEOS.gastos ?? [],
  },
  {
    id: 'analisis',
    paths: ['/analisis'],
    title: 'Análisis',
    summary:
      'Qué platos aportan más al negocio (ingeniería de menú) y cuánto necesitas vender para no perder dinero (punto de equilibrio).',
    howItWorks: [
      'El punto de equilibrio sale de tus costes fijos y del margen de contribución del periodo: es la venta mínima para cubrirlo todo. El margen de seguridad dice cuánto podrían caer las ventas antes de llegar ahí.',
      'La ingeniería de menú clasifica cada plato por grupo de carta según lo que se vende y el margen que deja: Estrella (se vende y deja margen), Caballo de batalla (se vende mucho, deja poco), Puzzle (buen margen, pocas ventas) y Perro (poco de ambas). Cada cuadrante trae su recomendación.',
      'Para clasificar hacen falta las unidades vendidas por plato. Si tu TPV está conectado, se traen con el botón sobre el periodo que estás mirando. Si no, se importan de un archivo.',
    ],
    faqs: [
      {
        q: '¿Por qué un plato sale como Perro?',
        a: 'Porque, comparado con los de su mismo grupo de carta, se vende poco y deja poco margen. No lo quites sin más: mira si comparte ingredientes, si cumple una función (infantil, alergias) o si el precio está mal.',
      },
      {
        q: 'No me sale la matriz.',
        a: 'Faltan las unidades vendidas del periodo. Tráelas del TPV o impórtalas; sin ellas no se puede saber qué se vende.',
      },
    ],
    suggestions: [
      '¿Qué platos son estrella y cuáles debería revisar?',
      '¿Cuánto tengo que vender al mes para cubrir costes?',
      '¿Cómo cargo las unidades vendidas por plato?',
    ],
    videos: VIDEOS.analisis ?? [],
  },
  {
    id: 'resultados',
    paths: ['/resultados'],
    title: 'Cuenta de resultados',
    summary:
      'De las ventas al beneficio neto, paso a paso: materia prima, personal, gastos generales, EBITDA, amortizaciones y resultado.',
    howItWorks: [
      'Aquí puedes ver cómo tus ventas se convierten en beneficio a medida que se descuentan los diferentes costes y gastos del negocio.',
      'Selecciona el periodo que quieras analizar. Alexia construirá automáticamente la cuenta de resultados con los ingresos, gastos e inversiones que tengas registrados.',
      'En la cascada del resultado puedes ver de forma rápida el recorrido desde los ingresos hasta el beneficio neto.',
      'Más abajo tienes el detalle completo, donde puedes desplegar partidas como consumo de materia prima, coste de personal y gastos generales para ver qué está provocando cada resultado.',
      'Además del importe, Alexia te muestra qué porcentaje de tus ingresos representa cada partida.',
      'Al final de la página puedes comparar tus resultados mes a mes y ver cómo evolucionan tus ingresos, EBITDA, beneficio neto y rentabilidad.',
    ],
    faqs: [
      {
        q: '¿De dónde salen los datos de esta cuenta de resultados?',
        a: 'Alexia utiliza la información que tienes registrada en otras secciones. Los ingresos vienen de Ingresos, los gastos de Gastos y las depreciaciones y amortizaciones de Inversiones. Si falta información en alguna de estas secciones, también afectará al resultado que veas aquí.',
      },
      {
        q: '¿Por qué el beneficio no coincide con el dinero que tengo en el banco?',
        a: 'Porque beneficio y tesorería no son lo mismo. La cuenta de resultados mide si el negocio ha generado beneficio durante un periodo, pero el saldo del banco también depende de cuándo cobras y pagas, préstamos, inversiones, impuestos y otros movimientos de dinero. Por eso puedes tener beneficio y no tener ese mismo importe disponible en la cuenta bancaria.',
      },
      {
        q: '¿Qué es el margen bruto?',
        a: 'Es lo que queda de tus ventas después de descontar el coste directamente relacionado con lo que vendes, principalmente el consumo de materia prima. Te ayuda a entender cuánto dinero queda disponible para cubrir el resto de gastos del negocio.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: 'El Prime Cost reúne dos de los costes más importantes de un restaurante: consumo de materia prima más coste de personal. Sirve para ver rápidamente qué parte de tus ventas estás destinando a producto y equipo.',
      },
      {
        q: '¿Qué es el EBITDA?',
        a: 'Es el resultado del negocio después de descontar sus costes y gastos operativos, pero antes de tener en cuenta depreciaciones, amortizaciones, intereses e impuestos. Es útil para analizar cómo está funcionando la operación del restaurante antes de esos conceptos.',
      },
      {
        q: '¿Qué diferencia hay entre EBITDA, EBIT y beneficio neto?',
        a: 'El EBITDA muestra el resultado operativo antes de depreciaciones, amortizaciones, intereses e impuestos. El EBIT ya descuenta depreciaciones y amortizaciones. Y el beneficio neto es el resultado final después de tener en cuenta también gastos financieros e impuestos.',
      },
      {
        q: '¿Por qué aparece una depreciación si este mes no compré nada?',
        a: 'Porque las inversiones no se cargan completas en el mes en que se compran. Alexia distribuye su coste a lo largo de su vida útil y refleja periódicamente esa parte como depreciación o amortización. Puedes consultar el detalle desde Inversiones.',
      },
      {
        q: '¿Por qué las cortesías y descuentos aparecen pero no se restan?',
        a: 'Porque se muestran como información para que puedas controlar su peso, pero no se vuelven a descontar si las ventas que has registrado ya reflejan ese efecto. Así evitamos restarlos dos veces.',
      },
      {
        q: '¿Cómo sé qué está haciendo bajar mi beneficio?',
        a: 'Empieza comparando el periodo con otros meses y después revisa qué partidas han aumentado como porcentaje de ventas. Puedes desplegar materia prima, personal y gastos generales para encontrar dónde está la desviación. También puedes preguntarle directamente a Alexia para que te ayude a interpretar los cambios.',
      },
    ],
    suggestions: [
      '¿Por qué he ganado menos este mes que el anterior?',
      '¿Qué porcentaje de las ventas se va en personal?',
      '¿Cuál es mi EBITDA del año?',
    ],
    videos: VIDEOS.resultados ?? [],
  },
  {
    id: 'inversiones',
    paths: ['/inversiones'],
    title: 'Inversiones',
    summary:
      'Equipos, mobiliario y reformas. Su coste se reparte a lo largo de su vida útil y solo la parte del mes entra en resultados.',
    howItWorks: [
      'Das de alta la inversión con su importe, la fecha y los años de vida útil. Alexia calcula la cuota mensual de amortización y la lleva a la cuenta de resultados cada mes, debajo del EBITDA.',
      'El cuadro de depreciación enseña, mes a mes, cuánto llevas amortizado y cuánto queda.',
    ],
    faqs: [
      {
        q: '¿Cuántos años pongo?',
        a: 'Orientativo: maquinaria de cocina 8-10 años, mobiliario 10, informática 4, reformas 10-20. Tu gestoría te dirá el criterio fiscal exacto; aquí importa que el mes cargue una parte razonable.',
      },
    ],
    suggestions: ['¿Cuánto amortizo al mes?', '¿Qué inversiones tengo activas?'],
    videos: VIDEOS.inversiones ?? [],
  },
  {
    id: 'objetivos',
    paths: ['/objetivos'],
    title: 'Objetivos',
    summary:
      'Lo que quieres conseguir cada mes: ventas, ticket, food cost, personal, prime cost y EBITDA. El panel y Alexia comparan el real con esto.',
    howItWorks: [
      'Aquí puedes definir los objetivos que quieres seguir cada mes y compararlos con los resultados reales de tu negocio.',
      'Selecciona el mes y completa únicamente las métricas que te interese controlar, como ventas, ticket promedio, EBITDA, compras, coste de materia prima, coste de personal, Prime Cost o margen de seguridad.',
      'No es necesario rellenarlas todas. Los campos que dejes en blanco simplemente no se incluirán en la comparación.',
      'Cuando guardes los objetivos, Alexia los comparará con los datos reales de ese mes y te mostrará la desviación y si cada objetivo se está cumpliendo o no.',
      'Estos objetivos también se utilizan en el panel general para señalar qué métricas están dentro de objetivo y cuáles necesitan tu atención.',
      'Puedes definir objetivos diferentes para cada mes.',
    ],
    faqs: [
      {
        q: '¿Tengo que completar todos los objetivos?',
        a: 'No. Solo necesitas definir las métricas que realmente quieras seguir. Si dejas un campo en blanco, Alexia no lo incluirá en la comparación de ese mes.',
      },
      {
        q: '¿Puedo tener objetivos diferentes cada mes?',
        a: 'Sí. Los objetivos se guardan por mes, así que puedes adaptarlos a la temporada, al presupuesto o a la situación concreta de tu negocio.',
      },
      {
        q: '¿De dónde salen los resultados reales?',
        a: 'Alexia utiliza los datos que tienes registrados durante ese mes en las diferentes secciones de la plataforma. Por eso, si faltan ingresos, gastos u otra información necesaria, el resultado real también puede quedar incompleto.',
      },
      {
        q: '¿Qué significa «desviación»?',
        a: 'Es la diferencia entre el objetivo que definiste y el resultado real. Te permite ver rápidamente cuánto te estás alejando o acercando a lo que habías planificado.',
      },
      {
        q: '¿Qué significa «desviación relativa»?',
        a: 'Muestra esa diferencia en porcentaje respecto al objetivo. Así puedes comparar mejor desviaciones entre métricas que tienen escalas diferentes.',
      },
      {
        q: '¿Por qué algunas métricas se cumplen al estar por encima y otras al estar por debajo?',
        a: 'Porque no todos los objetivos funcionan igual. Por ejemplo, normalmente quieres que las ventas o el EBITDA sean iguales o superiores al objetivo. En cambio, en métricas como coste de materia prima, coste de personal o Prime Cost, el objetivo suele funcionar como un máximo, por lo que se cumple cuando el resultado real es igual o inferior.',
      },
      {
        q: '¿Qué diferencia hay entre compras de materia prima y coste de materia prima?',
        a: 'Las compras de materia prima indican cuánto dinero has destinado a comprar producto durante el mes. El coste de materia prima refleja cuánto representa el consumo de materia prima sobre tus ventas. No son exactamente lo mismo, especialmente si trabajas con inventario.',
      },
      {
        q: '¿Qué es el Prime Cost?',
        a: 'Es la suma del coste de materia prima y el coste de personal. Te permite controlar cuánto de tus ventas estás destinando conjuntamente a producto y equipo.',
      },
      {
        q: '¿Qué es el margen de seguridad?',
        a: 'Indica cuánto podrían caer tus ventas antes de llegar al punto de equilibrio. Si defines un objetivo, Alexia comparará el margen real del mes con el mínimo que quieres mantener.',
      },
      {
        q: '¿Los objetivos cambian mis datos?',
        a: 'No. Los objetivos solo sirven como referencia para comparar tus resultados reales. No modifican ingresos, gastos, costes ni ninguna otra información de Alexia.',
      },
      {
        q: '¿Puedo cambiar un objetivo después de guardarlo?',
        a: 'Sí. Vuelve al mes correspondiente, modifica el valor que quieras y guarda de nuevo los objetivos.',
      },
    ],
    suggestions: [
      '¿Qué objetivos estoy cumpliendo este mes?',
      '¿Qué objetivo de ventas sería razonable para el mes que viene?',
    ],
    videos: VIDEOS.objetivos ?? [],
  },
  {
    id: 'impuestos',
    paths: ['/impuestos'],
    title: 'Impuestos',
    summary:
      'Reparte un pago trimestral o anual entre los meses a los que corresponde, para que el mes del pago no cargue con todo.',
    howItWorks: [
      'Aquí puedes repartir entre varios meses un pago de impuestos que hayas realizado en una sola fecha.',
      'Esto sirve para evitar que todo el importe afecte únicamente al mes en el que hiciste el pago cuando, en realidad, corresponde a varios periodos.',
      'Indica el concepto, la partida de gasto a la que corresponde, el importe pagado, la fecha del pago, el primer mes al que corresponde y cuántos meses quieres cubrir.',
      'Después selecciona la base de reparto. Puedes distribuir el importe según el peso de las ventas o de la nómina de cada mes.',
      'Alexia calculará cuánto corresponde a cada periodo y llevará esa parte a tu cuenta de resultados, en la partida que hayas elegido.',
      'Esta sección sirve para mejorar el análisis interno de tus resultados; no sustituye el criterio fiscal o contable que corresponda a cada impuesto.',
    ],
    faqs: [
      {
        q: '¿Por qué repartir un impuesto entre varios meses?',
        a: 'Porque la fecha en la que realizas el pago no siempre coincide con los meses a los que corresponde. Si pagas en septiembre un importe correspondiente a junio, julio y agosto, cargarlo entero en septiembre podría distorsionar la comparación entre meses.',
      },
      {
        q: '¿Qué significa «base de reparto»?',
        a: 'Es la referencia que Alexia utilizará para decidir qué parte del pago corresponde a cada mes. Puedes repartirlo según el peso de las ventas o de la nómina de cada periodo. No significa necesariamente que todos los meses reciban exactamente la misma cantidad.',
      },
      {
        q: '¿Cuándo utilizo ventas y cuándo nómina?',
        a: 'Utiliza la opción que mejor represente aquello con lo que está relacionado el pago. Si quieres distribuirlo según la actividad o facturación de cada mes, puedes utilizar Ventas. Si el pago está relacionado con el coste de personal, puedes utilizar Nómina.',
      },
      {
        q: '¿Alexia divide siempre el importe a partes iguales?',
        a: 'No necesariamente. Si seleccionas una base de reparto, Alexia tendrá en cuenta el peso que esa métrica tuvo en cada mes. Por ejemplo, un mes con más ventas puede recibir una parte mayor del importe que otro con menos ventas.',
      },
      {
        q: '¿Qué pongo como primer mes del periodo?',
        a: 'El primer mes al que realmente quieres atribuir parte de ese pago. Después indica cuántos meses cubre y Alexia te mostrará el periodo completo antes de repartirlo.',
      },
      {
        q: 'Pagué un trimestre completo de una sola vez, ¿lo registro todo en el mes del pago?',
        a: 'Si ese pago corresponde realmente a varios meses y quieres que tu análisis mensual refleje esa distribución, puedes utilizar esta sección para repartirlo entre los periodos correspondientes. Así evitas que un único mes quede artificialmente cargado con todo el importe.',
      },
      {
        q: '¿El IVA se registra aquí como un gasto?',
        a: 'El IVA tiene un tratamiento diferente al de un gasto corriente y no debería repartirse aquí automáticamente como si fuera un coste del negocio. Si tienes dudas sobre cómo debe tratarse en tu caso, consulta con tu asesor fiscal.',
      },
      {
        q: '¿Puedo cambiar un reparto después de hacerlo?',
        a: 'No directamente desde esta sección. Cuando repartes un pago, Alexia crea automáticamente los gastos correspondientes en los meses que hayas indicado. Si necesitas corregirlo, ve a Gastos, busca los movimientos generados en cada mes y elimínalos. Después puedes volver a Impuestos y hacer el reparto de nuevo con los datos correctos.',
      },
    ],
    suggestions: ['¿Cómo reparto el pago de un trimestre?', '¿Qué impuestos tengo periodificados este año?'],
    videos: VIDEOS.impuestos ?? [],
  },
  {
    id: 'ajustes',
    paths: ['/ajustes'],
    title: 'Ajustes',
    summary:
      'Los datos del restaurante, los parámetros con los que calcula Alexia, el TPV conectado, el plan y el equipo.',
    howItWorks: [
      'Mi restaurante: nombre, país, moneda, locales y los datos de facturación de tu suscripción.',
      'Parámetros de venta: el IVA de ventas, la comisión del datáfono, las plataformas de delivery con su comisión, el sistema de medidas, los grupos de ingredientes y de gasto, y los umbrales de alerta (desviación de coste y margen bruto mínimo).',
      'Integraciones: conectar Loyverse o Square para que las ventas entren solas, decir a qué local va cada tienda y traer un rango de ventas.',
      'Plan y módulos: tu plan, tus facturas, el módulo de escaneo de facturas, usuarios y locales adicionales.',
      'Equipo: invitar al encargado (ve la operación, no los resultados) o a la gestoría (lo ve todo, no cambia nada).',
    ],
    faqs: [
      {
        q: 'He cambiado el IVA, ¿se recalcula todo?',
        a: 'Sí, al momento: todos los márgenes de la carta se recalculan con el IVA nuevo. Lo que ya tenías registrado en ingresos no cambia.',
      },
      {
        q: '¿Puedo tener varios locales?',
        a: 'Todos los planes incluyen uno; los adicionales se contratan desde aquí. Cada local lleva sus ingresos y gastos, y el selector de arriba elige cuál miras.',
      },
      {
        q: '¿Cómo cambio la contraseña?',
        a: 'En Mi cuenta. Te enviamos un código al correo y pones la nueva.',
      },
    ],
    suggestions: [
      '¿Qué umbral de alerta de coste me conviene?',
      '¿Cómo conecto mi TPV?',
      '¿Qué puede ver el encargado y qué no?',
    ],
    videos: [],
  },
];

/** Pantalla a la que pertenece una ruta del frontend, o undefined si no tiene manual. */
export function helpScreenForPath(pathname: string): HelpScreen | undefined {
  const limpio = pathname.replace(/\/+$/, '') || '/';
  let mejor: HelpScreen | undefined;
  let largo = -1;
  for (const screen of HELP_SCREENS) {
    for (const path of screen.paths) {
      const coincide = path === '/' ? limpio === '/' : limpio === path || limpio.startsWith(`${path}/`);
      if (coincide && path.length > largo) {
        mejor = screen;
        largo = path.length;
      }
    }
  }
  return mejor;
}

export function helpScreenById(id: string): HelpScreen | undefined {
  return HELP_SCREENS.find((screen) => screen.id === id);
}

/**
 * Manual de UNA pantalla en texto plano, para el prompt de Alexia. Va sin
 * los vídeos y sin las sugerencias: es lo que el modelo necesita para
 * contestar «cómo hago X aquí».
 */
export function helpScreenAsText(screen: HelpScreen): string {
  const lineas = [
    `## ${screen.title}`,
    screen.summary,
    '',
    ...screen.howItWorks.map((p) => `- ${p}`),
  ];
  if (screen.faqs.length > 0) {
    lineas.push('', 'Preguntas frecuentes:');
    for (const faq of screen.faqs) lineas.push(`- ${faq.q} ${faq.a}`);
  }
  return lineas.join('\n');
}

/** Índice de una línea por pantalla: para que Alexia sepa mandar a cada sitio. Es estático: se calcula una vez. */
export const HELP_INDEX_TEXT: string = HELP_SCREENS.map((screen) => `- ${screen.title}: ${screen.summary}`).join('\n');

export function helpIndexAsText(): string {
  return HELP_INDEX_TEXT;
}
